﻿import { Box, Button, Container, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useGridApiRef, GridToolbar } from "@mui/x-data-grid";
import {
  ShepDataGridPro,
  ShepPagination,
  ShepSearchBar,
  ShepCheckbox,
} from "@/components/GenericComponents/ShepComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  addPendingMappedSourceCatalogs,
  selectConnectionCatalogs,
  selectCatalogFilter,
  selectIsAddCrossRefOpen,
  selectMappedSourceCatalogs,
  selectXRefType,
  selectMapOnFlyInfo,
  DefaultMappingTypes,
  fetchSourceConnectionCatalogs,
  fetchTargetConnectionCatalogs,
  removeMappedSourceCatalog,
} from "@/features/connections/crossReferenceSlice";
import { createAlert } from "@/features/homepage/alertSliceList";
import { CrossReferenceMappingInsertType, EmptyGuid  } from "@/utils/utils";

const ConnectionNameGridTitle = ({ title }) => {
  return (
    <Box
      sx={{
        height: "78px",
        backgroundColor: "var(--primary-main)",
        color: "var(--default-light)",
        padding: "20px 0px 20px 10px",
        borderRadius: "4px",
        border: "1px black",
        boxSizing: "border-box",
      }}
    >
      <Typography
        style={{
          fontSize: "var(--font-lg)",
          fontWeight: "var(--font-semi-bold)",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const applyFilter = ({ gridFilter, dataSet }) => {
  let result = [];
  gridFilter.forEach((currentFilter) => {
    const paths = currentFilter.path.split(".");
    let filtered = [];
    if (paths.length > 1) {
      filtered =
        dataSet?.filter(
          (row) => JSON.parse(row[paths[0]])[paths[1]] == currentFilter.value,
        ) ?? [];
    } else {
      filtered =
        dataSet?.filter((row) => row[paths[0]] == currentFilter.value) ?? [];
    }
    result = [...result, ...filtered];
  });
  return result;
};
const CatalogMappingGrids = ({
  sourceConnection,
  targetConnection,
  descriptionField,
  children,
  onSourceGridRowClick, // Callback Fn
  catalogType,
  onSourceSearchTextChange,
  onTargetSearchTextChange,
  sourceGridLoaded,
  targetGridLoaded,
  customFetchTargetCatalog,
}) => {
  const sourceGridRef = useGridApiRef();
  const targetGridRef = useGridApiRef();
  const [sourceCatalogSelected, setSourceCatalogSelected] = useState("");
  const [targetCatalogSelected, setTargetCatalogSelected] = useState("");
  const [showMappedCatalogs, setShowMappedCatalogs] = useState(true);
  const [targetCatalogsToDisplay, setTargetCatalogsToDisplay] = useState([]);
  const [crossReferenceJSONProperties, setCrossReferenceJSONProperties] =
    useState({});
  const targetConnCatalogs = useSelector(
    selectConnectionCatalogs,
  )?.targetCatalogs;
  const sourceCatalogs = useSelector(selectConnectionCatalogs)?.sourceCatalogs;
  const catalogFilter = useSelector(selectCatalogFilter);
  const isAddCrossRef = useSelector(selectIsAddCrossRefOpen);
  const mappedSourceCatalogs = useSelector(selectMappedSourceCatalogs);
  const [targetSearchText, setTargetSearchText] = useState("");
  const [sourceSearchText, setSourceSearchText] = useState("");
  const [unmappedItems, setUnmappedItems] = useState([]);
  const selectedXRef = useSelector(selectXRefType);
  const mapOnFlyInfo = useSelector(selectMapOnFlyInfo);

  const dispatch = useDispatch();
  useEffect(() => {
    //allow child components to be re-rendered on selection change
  }, [sourceCatalogSelected, targetCatalogSelected]);
  useEffect(() => {
    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return sourceGridRef.current.subscribeEvent("rowClick", (e) => {
      if (e.row.DefaultMappingType != DefaultMappingTypes.Invalid) {
        setSourceCatalogSelected(e.row.CatalogId);
      } else {
        setSourceCatalogSelected("");
      }
    });
  }, [sourceGridRef]);
  useEffect(() => {
    // The `subscribeEvent` method will automatically unsubscribe in the cleanup function of the `useEffect`.
    return targetGridRef.current.subscribeEvent("rowClick", (e) =>
      setTargetCatalogSelected(e.row.CatalogId),
    );
  }, [targetGridRef]);
  useEffect(() => {
    const catalogsToDisplay = showMappedCatalogs
      ? targetConnCatalogs
      : targetConnCatalogs.filter(
          (x) =>
            x.CrossReferenceMappingId === null && x.MappedCatalogId === null,
        );

    if (
      catalogFilter.targetFilter !== undefined &&
      catalogFilter.targetFilter.length != 0
    ) {
      const filteredCatalogs = applyFilter({
        gridFilter: catalogFilter.targetFilter,
        dataSet: catalogsToDisplay,
      });
      setTargetCatalogsToDisplay(filteredCatalogs);
      if (
        filteredCatalogs.find((x) => x.CatalogId == targetCatalogSelected) ==
        undefined
      ) {
        setTargetCatalogSelected("");
      }
    } else {
      setTargetCatalogsToDisplay(catalogsToDisplay);
    }
  }, [showMappedCatalogs, targetConnCatalogs, catalogFilter, targetSearchText]);

  useEffect(() => {
    const unmapped = sourceCatalogs;
    if (
      catalogFilter.sourceFilter !== undefined &&
      catalogFilter.sourceFilter.length != 0
    ) {
      // Run the custom applyFilter exactly as it is
      let filteredCatalogs = applyFilter({
        gridFilter: catalogFilter.sourceFilter,
        dataSet: unmapped,
      });
      /**
       * Check if the original data had the default values.
       * MapOnFly screen was skippig some showing the normal DEFAULT
       * as well as the invalid DEFAULT cases for ShipTo and UOM
       * due to the catalogFilter. This change is used to preserve
       * such DEFAULTs if and only if they exist.
       */
      const defaultCatalogs = unmapped.filter((x) => x.CatalogId === EmptyGuid);

      // If defaults exist, guarantee they are in the final result
      if (defaultCatalogs.length > 0) {
        // Clean up the filtered list so we don't accidentally create duplicates
        filteredCatalogs = filteredCatalogs.filter(
          (x) => x.CatalogId !== EmptyGuid,
        );
        // Combine them: Defaults first, then the filtered results
        filteredCatalogs = [...defaultCatalogs, ...filteredCatalogs];
      }

      setUnmappedItems(filteredCatalogs);
      if (
        filteredCatalogs.find((x) => x.CatalogId == sourceCatalogSelected) ==
        undefined
      ) {
        setSourceCatalogSelected("");
      }
    } else {
      setUnmappedItems(unmapped);
    }
  }, [sourceCatalogs, catalogFilter, mappedSourceCatalogs]);

  const sourceConnectionColumns = React.useMemo(() => [
    {
      field: "",
      headerName: "Name",
      flex: 1,
      minHeight: 80,
      sortable: true,
      valueGetter: (params) => {
        return params.row.CatalogReference;
      },
      renderCell: (params) => {
        const description = JSON.parse(params.row.ArtifactData)[
          descriptionField
        ];

        return (
          <Box
            className={
              params.row.DefaultMappingType == DefaultMappingTypes.Invalid
                ? "invalid"
                : ""
            }
            sx={{
              maxHeight: "75px",
              overflowY: "auto",
              width: "100%",
              pr: 1,
            }}
          >
            <Typography>{params.row.CatalogReference}</Typography>
            <Typography>{description}</Typography>
            {params.row?.ErrorMessage && (
              <Box
                component="ul"
                sx={{
                  mt: 0.5,
                  mb: 0,
                  pl: 2,
                }}
              >
                {params.row.ErrorMessage.split(".")
                  .map((msg) => msg.trim())
                  .filter((msg) => msg.length > 0)
                  .map((msg, index) => (
                    <Typography
                      component="li"
                      key={index}
                      sx={{
                        display: "list-item",
                        lineHeight: 1.2,
                        mb: 0.5,
                      }}
                    >
                      {msg}.
                    </Typography>
                  ))}
              </Box>
            )}
          </Box>
        );
      },
      valueFormatter: (params) => {
        return params.value ?? "";
      },
    },
  ]);
  const targetConnectionColumns = React.useMemo(() => [
    {
      field: "display",
      headerName: "Name",
      flex: 1,
      minHeight: 80,
      sortable: true,
      valueGetter: (params) => {
        return params.row.CatalogReference;
      },
      renderCell: (params) => {
        const description = JSON.parse(params.row.ArtifactData)[
          descriptionField
        ];

        return (
          <Box
            className={
              params.row.MappedCatalogReference == null ? "" : "mapped"
            }
          >
            <Typography>{params.row.CatalogReference}</Typography>
            <Typography>{description}</Typography>
            <Typography>{params.row.MappedCatalogReference}</Typography>
          </Box>
        );
      },
      valueFormatter: (params) => {
        return params.value ?? "";
      },
    },
  ]);

  const dispatchAddPendingMappedCatalogue = async ({insertType}) => {
    const linkedMappedCatalogs = mappedSourceCatalogs.filter(catalog => catalog.MappedCatalogId == targetCatalogSelected);
    dispatch(
      addPendingMappedSourceCatalogs({
        sourceId: sourceCatalogSelected,
        targetId: targetCatalogSelected,
        xrefJSON: crossReferenceJSONProperties,
        insertType
      })
    );
    if (insertType == CrossReferenceMappingInsertType.Replace) {
      for (const catalog of linkedMappedCatalogs) {
        dispatch(
          removeMappedSourceCatalog({
            sourceId: catalog.CatalogId,
            targetId: catalog.MappedCatalogId,
          })
        );
      }
    }
    setSourceCatalogSelected("");
    setTargetCatalogSelected("");
  }
  const chooseInsertPriority = async () => {
    await dispatch(
      createAlert({
        status: "Info",
        alertType: "result",
        message: 'Choose insert priority.',
        buttonLabel: "High",
        onButtonClick: () => {
          dispatchAddPendingMappedCatalogue({
            insertType: CrossReferenceMappingInsertType.HighPriority,
          })
        },
        buttonLabel2: "Low",
        onButtonClick2: () => {
          dispatchAddPendingMappedCatalogue({
            insertType: CrossReferenceMappingInsertType.LowPriority,
          })
        }
      })
    )
  }

  const AddToPending = async () => {
    const targetCatalogue = targetConnCatalogs.find(
          (x) => x.CatalogId === targetCatalogSelected
        );
    const targetCatalogueHasExistingMappings = !!(
      (targetCatalogue.CrossReferenceMappingId && targetCatalogue.MappedCatalogId) ||
      mappedSourceCatalogs.find(mc => mc.MappedCatalogId == targetCatalogue.CatalogId)
    );

    const catalogIsDefault = sourceCatalogSelected === EmptyGuid;
    if (!catalogIsDefault && targetCatalogueHasExistingMappings) {
      await dispatch(
        createAlert({
          status: "Info",
          alertType: "result",
          message: `Many to one mapping detected.`,
          buttonLabel: "Add",
          onButtonClick: () => {
            chooseInsertPriority();
          },
          buttonLabel2: "Replace",
          onButtonClick2: () => {
            dispatchAddPendingMappedCatalogue({
              insertType: CrossReferenceMappingInsertType.Replace,
            })
          }
        })
      );
    } else if (catalogIsDefault) {
      await dispatchAddPendingMappedCatalogue({
        insertType: CrossReferenceMappingInsertType.Exempt,
      });
    } else {
      await dispatchAddPendingMappedCatalogue({
        insertType: CrossReferenceMappingInsertType.None,
      });
    }
  }

  const handleShowMappedCatalogs = () => {
    setShowMappedCatalogs(!showMappedCatalogs);
  };

  const onSourceRowClick = (row) => {
    if (onSourceGridRowClick) {
      onSourceGridRowClick({ row });
    }
  };

  const fetchSourceRecords = (sourceFilter) => {
    dispatch(
      fetchSourceConnectionCatalogs({
        connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
        crossReferenceType: selectedXRef,
        sourceConnectionId: mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
        targetConnectionId: mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
        searchFilter: sourceFilter || null,
      }),
    );
  };

  const fetchTargetRecords = (targetFilter) => {
    if (customFetchTargetCatalog) {
      dispatch(
        customFetchTargetCatalog({
          connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
          crossReferenceType: selectedXRef,
          sourceConnectionId:
            mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
          targetConnectionId:
            mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
          searchFilter: targetFilter,
        }),
      );
    } else {
      dispatch(
        fetchTargetConnectionCatalogs({
          connectionPairId: mapOnFlyInfo.crossReferenceInfo.ConnectionPairId,
          crossReferenceType: selectedXRef,
          sourceConnectionId:
            mapOnFlyInfo.crossReferenceInfo.SourceConnectionId,
          targetConnectionId:
            mapOnFlyInfo.crossReferenceInfo.TargetConnectionId,
          searchFilter: targetFilter || null,
        }),
      );
    }
  };

  return (
    <>
      <Container sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "right",
            padding: "50px 5% 0px 0px",
            alignItems: "center",
          }}
        >
          <ShepCheckbox
            onChange={handleShowMappedCatalogs}
            checked={showMappedCatalogs}
          />
          {"Include Mapped Items"}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "10px 0px",
            paddingRight: "5%",
            paddingLeft: "5%",
          }}
        >
          <ShepSearchBar
            placeholder="Search Source Catalogs"
            sx={{ width: "44.5%" }}
            value={sourceSearchText}
            onChange={(event) => {
              const value = event.target.value;
              setSourceSearchText(value); // just update state while typing

              if (onSourceSearchTextChange) {
                onSourceSearchTextChange(value);
              }
              // If user clears the input, reset the grid immediately
              if (value.trim() === "") {
                fetchSourceRecords("");
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.defaultPrevented = true; // Prevent's default 'Enter' behavior.
                fetchSourceRecords(sourceSearchText);
              }
            }}
          />
          <ShepSearchBar
            placeholder="Search Target Catalogs"
            sx={{ width: "44.5%" }}
            value={targetSearchText}
            onChange={async (event) => {
              const value = event.target.value;
              setTargetSearchText(value);

              if (onTargetSearchTextChange) {
                await onTargetSearchTextChange(value);
              }
              // If user clears the input, reset the grid immediately
              if (value.trim() === "") {
                fetchTargetRecords("");
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.defaultPrevented = true; // Prevent's default 'Enter' behavior.
                fetchTargetRecords(targetSearchText);
              }
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-around",
          }}
        >
          <Container sx={{ width: "40%" }}>
            <ConnectionNameGridTitle title={sourceConnection} />
            <ShepDataGridPro
              apiRef={sourceGridRef}
              isDataLoaded={sourceGridLoaded}
              rows={unmappedItems ?? []}
              rowHeight={80}
              columns={sourceConnectionColumns}
              columnHeaderHeight={78}
              getRowId={(row) => {
                if (
                  row.DefaultMappingType === DefaultMappingTypes.Default ||
                  row.DefaultMappingType === DefaultMappingTypes.Invalid
                ) {
                  //entries with empty guid
                  return (
                    row.CatalogId.toString() +
                    row.DefaultMappingType.toString() +
                    row.CatalogReference
                  );
                }
                return row.CatalogId.toString();
              }}
              onRowClick={(e) => onSourceRowClick(e.row)}
              disableColumnMenu
              pagination
              slots={{
                pagination: () => ShepPagination({ pageOptions: [5, 10, 15] }),
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5, page: 0 },
                },
              }}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              hideFooterSelectedRowCount
              sx={{
                height: "545px",
                fontSize: "var(--font-sm)",
                fontFamily: "var(--font-family)",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                backgroundColor: "var(--default-light)",
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "var(--tertiary-13)",
                },
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-pale)",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "var(--font-md)",
                  fontWeight: "var(--font-bold)",
                  borderLeft: "none",
                },
                "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                  {
                    borderBottomLeftRadius: "4px",
                    borderBottomRightRadius: "4px",
                    backgroundColor: "var(--primary-pale)",
                    borderBottom: "none",
                    borderLeft: "none",
                  },
              }}
            />
          </Container>
          <Container sx={{ width: "40%" }}>
            <ConnectionNameGridTitle title={targetConnection} />
            <ShepDataGridPro
              apiRef={targetGridRef}
              isDataLoaded={targetGridLoaded}
              rows={targetCatalogsToDisplay ?? []}
              rowHeight={80}
              columns={targetConnectionColumns}
              columnHeaderHeight={78}
              disableColumnMenu
              getRowId={(row) => row.CatalogId}
              pagination
              slots={{
                pagination: () => ShepPagination({ pageOptions: [5, 10, 15] }),
                toolbar: GridToolbar,
              }}
              slotProps={{
                toolbar: {
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: true },
                },
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 5, page: 0 },
                },
                columns: {
                  columnVisibilityModel: {
                    mappedCatalogId: false,
                  },
                },
              }}
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              hideFooterSelectedRowCount
              sx={{
                maxHeight: "545px",
                fontSize: "var(--font-sm)",
                fontFamily: "var(--font-family)",
                borderTopLeftRadius: "12px",
                borderTopRightRadius: "12px",
                backgroundColor: "var(--default-light)",
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "var(--tertiary-13)",
                },
                "& .Mui-selected": {
                  backgroundColor: "var(--primary-pale)",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "var(--font-md)",
                  fontWeight: "var(--font-bold)",
                  borderLeft: "none",
                },
                "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                  {
                    borderBottomLeftRadius: "4px",
                    borderBottomRightRadius: "4px",
                    backgroundColor: "var(--primary-pale)",
                    borderBottom: "none",
                    borderLeft: "none",
                  },
              }}
            />
          </Container>
        </Box>
        <Box>
          {React.Children.map(children, (child) =>
            React.isValidElement(child)
              ? React.cloneElement(child, {
                  sourceGridRef,
                  targetGridRef,
                  crossRefProperties: crossReferenceJSONProperties,
                  setCrossRefProperties: setCrossReferenceJSONProperties,
                })
              : child,
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "right",
            padding: "50px 5% 50px 0",
          }}
        >
          <Button
            className="btn-primary"
            onClick={() => AddToPending()}
            disabled={!(sourceCatalogSelected && targetCatalogSelected)}
          >
            Map
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default CatalogMappingGrids;
